/**
 * Enhanced Pricing Validation Service
 * Provides comprehensive validation and calculation logic for project pricing
 */

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  field: string;
  message: string;
  type: 'required' | 'format' | 'range' | 'business';
}

export interface ValidationWarning {
  field: string;
  message: string;
  type: 'suggestion' | 'business';
}

export interface PricingCalculation {
  originalPrice: number;
  discountPercent: number;
  discountedPrice: number;
  savings: number;
  isValidCombination: boolean;
  displayFormat: 'single' | 'comparison';
}

export class PricingValidationService {
  /**
   * Validates proposed price field
   */
  static validateProposedPrice(value: string): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    // Allow empty values (optional field)
    if (!value || value.trim() === '') {
      return { isValid: true, errors, warnings };
    }

    const price = parseFloat(value);

    // Check if it's a valid number
    if (isNaN(price)) {
      errors.push({
        field: 'proposed_price',
        message: 'Proposed price must be a valid number',
        type: 'format'
      });
      return { isValid: false, errors, warnings };
    }

    // Check for negative values
    if (price < 0) {
      errors.push({
        field: 'proposed_price',
        message: 'Proposed price cannot be negative',
        type: 'range'
      });
    }

    // Check for zero value
    if (price === 0) {
      warnings.push({
        field: 'proposed_price',
        message: 'Consider if a free project should have an early bird discount',
        type: 'business'
      });
    }

    // Check for extremely high values
    if (price > 999999.99) {
      errors.push({
        field: 'proposed_price',
        message: 'Proposed price cannot exceed $999,999.99',
        type: 'range'
      });
    }

    // Business logic warnings
    if (price > 0 && price < 1) {
      warnings.push({
        field: 'proposed_price',
        message: 'Very low prices might not be taken seriously by potential customers',
        type: 'suggestion'
      });
    }

    if (price > 10000) {
      warnings.push({
        field: 'proposed_price',
        message: 'High-priced projects may need more detailed justification',
        type: 'suggestion'
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Validates early bird discount field
   */
  static validateEarlyBirdDiscount(value: string): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    // Allow empty values (optional field)
    if (!value || value.trim() === '') {
      return { isValid: true, errors, warnings };
    }

    const discount = parseFloat(value);

    // Check if it's a valid number
    if (isNaN(discount)) {
      errors.push({
        field: 'early_bird_discount',
        message: 'Early bird discount must be a valid number',
        type: 'format'
      });
      return { isValid: false, errors, warnings };
    }

    // Check range (0-100)
    if (discount < 0) {
      errors.push({
        field: 'early_bird_discount',
        message: 'Early bird discount cannot be negative',
        type: 'range'
      });
    }

    if (discount > 100) {
      errors.push({
        field: 'early_bird_discount',
        message: 'Early bird discount cannot exceed 100%',
        type: 'range'
      });
    }

    // Business logic warnings
    if (discount > 0 && discount < 5) {
      warnings.push({
        field: 'early_bird_discount',
        message: 'Very small discounts may not motivate early adoption',
        type: 'suggestion'
      });
    }

    if (discount > 75) {
      warnings.push({
        field: 'early_bird_discount',
        message: 'Very high discounts might devalue your project',
        type: 'business'
      });
    }

    if (discount === 100) {
      warnings.push({
        field: 'early_bird_discount',
        message: '100% discount means the project will be free for early birds',
        type: 'business'
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Validates pricing combination and calculates discounted price
   */
  static validatePricingCombination(
    proposedPrice: string,
    earlyBirdDiscount: string
  ): ValidationResult & { calculation?: PricingCalculation } {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    // First validate individual fields
    const priceValidation = this.validateProposedPrice(proposedPrice);
    const discountValidation = this.validateEarlyBirdDiscount(earlyBirdDiscount);

    errors.push(...priceValidation.errors);
    errors.push(...discountValidation.errors);
    warnings.push(...priceValidation.warnings);
    warnings.push(...discountValidation.warnings);

    // If individual validations failed, return early
    if (errors.length > 0) {
      return { isValid: false, errors, warnings };
    }

    // Parse values
    const price = proposedPrice && proposedPrice.trim() !== '' ? parseFloat(proposedPrice) : 0;
    const discount = earlyBirdDiscount && earlyBirdDiscount.trim() !== '' ? parseFloat(earlyBirdDiscount) : 0;

    // Business logic validation
    if (price === 0 && discount > 0) {
      warnings.push({
        field: 'early_bird_discount',
        message: 'Early bird discount on a free project has no effect',
        type: 'business'
      });
    }

    // Calculate discounted price if both values are provided
    let calculation: PricingCalculation | undefined;
    if (price > 0) {
      const discountedPrice = this.calculateDiscountedPrice(price, discount);
      const savings = price - discountedPrice;

      calculation = {
        originalPrice: price,
        discountPercent: discount,
        discountedPrice,
        savings,
        isValidCombination: discountedPrice >= 0,
        displayFormat: discount > 0 ? 'comparison' : 'single'
      };

      // Check for negative final price (shouldn't happen with proper validation, but safety check)
      if (discountedPrice < 0) {
        errors.push({
          field: 'early_bird_discount',
          message: 'Discount cannot result in a negative price',
          type: 'business'
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      calculation
    };
  }

  /**
   * Calculates the discounted price using the formula: original_price * (1 - discount/100)
   */
  static calculateDiscountedPrice(originalPrice: number, discountPercent: number): number {
    if (originalPrice <= 0 || discountPercent <= 0) {
      return originalPrice;
    }

    const discountMultiplier = 1 - (discountPercent / 100);
    const discountedPrice = originalPrice * discountMultiplier;
    
    // Round to 2 decimal places to avoid floating point precision issues
    return Math.round(discountedPrice * 100) / 100;
  }

  /**
   * Formats price for display
   */
  static formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  }

  /**
   * Generates pricing display information
   */
  static generatePricingDisplay(calculation: PricingCalculation): {
    originalPriceText: string;
    discountedPriceText?: string;
    savingsText?: string;
    displayText: string;
  } {
    const originalPriceText = this.formatPrice(calculation.originalPrice);

    if (calculation.displayFormat === 'single') {
      return {
        originalPriceText,
        displayText: originalPriceText
      };
    }

    const discountedPriceText = this.formatPrice(calculation.discountedPrice);
    const savingsText = this.formatPrice(calculation.savings);

    return {
      originalPriceText,
      discountedPriceText,
      savingsText,
      displayText: `Original: ${originalPriceText}, Early Bird: ${discountedPriceText} (Save ${savingsText})`
    };
  }

  /**
   * Real-time validation for form inputs
   */
  static validateFieldRealTime(
    field: 'proposed_price' | 'early_bird_discount',
    value: string,
    otherFieldValue?: string
  ): {
    isValid: boolean;
    error?: string;
    warning?: string;
    calculation?: PricingCalculation;
  } {
    if (field === 'proposed_price') {
      const validation = this.validateProposedPrice(value);
      const error = validation.errors[0]?.message;
      const warning = validation.warnings[0]?.message;

      // If we have both price and discount, calculate combination
      let calculation: PricingCalculation | undefined;
      if (validation.isValid && otherFieldValue) {
        const combinedValidation = this.validatePricingCombination(value, otherFieldValue);
        calculation = combinedValidation.calculation;
      }

      return {
        isValid: validation.isValid,
        error,
        warning,
        calculation
      };
    } else {
      const validation = this.validateEarlyBirdDiscount(value);
      const error = validation.errors[0]?.message;
      const warning = validation.warnings[0]?.message;

      // If we have both price and discount, calculate combination
      let calculation: PricingCalculation | undefined;
      if (validation.isValid && otherFieldValue) {
        const combinedValidation = this.validatePricingCombination(otherFieldValue, value);
        calculation = combinedValidation.calculation;
      }

      return {
        isValid: validation.isValid,
        error,
        warning,
        calculation
      };
    }
  }
}